import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ComboBox,
  Grid,
  Modal,
  ModalContent,
  SelectInput,
  TextArea,
  TextInput,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import {
  runNrtRecordTestCheck,
  useCreateNrtRecord,
  useUpdateNrtRecord
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import type {
  CreateNrtRecordRequest,
  NrtClassification,
  NrtRecord,
  NrtRecordFormValidationErrors,
  NrtStatus,
  NrtVehicleType,
  UpdateNrtRecordRequest
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  buildInitialNrtRecordFormState,
  buildInitialNrtTestResult,
  validateNrtRecordForm
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { parseNrtDate, toUnixTimestampString } from "./nrtDate";
import { SR_ONLY_SX } from "./srOnlySx";
import { STATUS_LABEL } from "./StatusChip";
import { TestResultPanel } from "./TestResultPanel";

export interface RecordModalProps {
  mode: "add" | "edit";
  initialValues?: NrtRecord;
  onClose: () => void;
}

const NOTES_ROWS = 4;

// type="date" only accepts YYYY-MM-DD; stored dates can be epoch-second.
const toDateInputValue = (value: string | null): string => {
  const date = parseNrtDate(value);
  return date ? date.toISOString().slice(0, 10) : (value ?? "");
};

// Record stores classification as one quoted, comma-separated string.
const CLASSIFICATION_SEPARATOR = ",";

const parseClassifications = (value: string | null): string[] =>
  (value ?? "")
    .split(CLASSIFICATION_SEPARATOR)
    .map(token => token.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);

const toClassificationOptions = (value: string | null) => {
  const selected = parseClassifications(value);
  return CLASSIFICATION_OPTIONS.filter(option =>
    selected.includes(option.value)
  );
};

const serializeClassifications = (
  options: readonly { value: string }[]
): string | null =>
  options.map(option => `'${option.value}'`).join(CLASSIFICATION_SEPARATOR) ||
  null;

const STATUS_OPTIONS: { label: string; value: NrtStatus }[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Testing", value: "TESTING" }
];

const CLASSIFICATION_OPTIONS: {
  label: string;
  value: Exclude<NrtClassification, null>;
}[] = [
  { label: "All", value: "ALL" },
  { label: "Primary", value: "PRIMARY" },
  { label: "Fleet", value: "FLEET" },
  { label: "Wholesale", value: "WHOLESALE" },
  { label: "Exotic", value: "EXOTIC" },
  { label: "Classifieds", value: "CLASSIFIEDS" }
];

const TYPE_OPTIONS: { label: string; value: Exclude<NrtVehicleType, null> }[] =
  [
    { label: "All", value: "ALL" },
    { label: "New", value: "NEW" },
    { label: "Used", value: "USED" }
  ];

export const RecordModal = ({
  mode,
  initialValues,
  onClose
}: RecordModalProps) => {
  const isAddMode = mode === "add";
  const [formState, setFormState] = useState(() => {
    const initialState = buildInitialNrtRecordFormState(initialValues);
    return {
      ...initialState,
      expirationDate: toDateInputValue(initialState.expirationDate)
    };
  });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasBlurredExpirationDate, setHasBlurredExpirationDate] =
    useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [testResult, setTestResult] = useState(buildInitialNrtTestResult);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { toastSuccess } = useToaster();
  const { mutateAsync: createNrtRecord, isPending: isCreating } =
    useCreateNrtRecord();
  const { mutateAsync: updateNrtRecord, isPending: isUpdating } =
    useUpdateNrtRecord();
  const isSaving = isCreating || isUpdating;

  const validationError = validateNrtRecordForm(formState);

  const classificationComboBoxValue = useMemo(
    () => toClassificationOptions(formState.classification),
    [formState.classification]
  );

  // FR-014: Test shares Save's whole-form validation gate.
  const isTestDisabled = !!validationError;

  // Locks the fields + close affordance while either action is in flight.
  // The action row itself is only hidden for a running Test (see below) —
  // Save shows its own disabled/spinner state instead, via SaveAndCancel.
  const isProcessing = testResult.status === "RUNNING" || isSaving;

  // Announces test outcomes via the polite region below (toaster has no aria-live).
  useEffect(() => {
    if (testResult.status === "IDLE") return;

    const label = STATUS_LABEL[testResult.status].toLowerCase();
    setAnnouncement(
      testResult.message
        ? `Test ${label}. ${testResult.message}`
        : `Test ${label}.`
    );
  }, [testResult]);

  // Errors stay hidden until the first Save attempt.
  const errorProps = useCallback(
    (field: keyof NrtRecordFormValidationErrors) => {
      const shouldShowError =
        field === "expirationDate"
          ? (hasBlurredExpirationDate || hasAttemptedSubmit) && validationError
          : hasAttemptedSubmit && validationError;
      const message = shouldShowError ? validationError?.[field] : undefined;
      return message ? { errorMessage: message, hasError: true } : {};
    },
    [hasAttemptedSubmit, hasBlurredExpirationDate, validationError]
  );

  const updateField = useCallback(
    <TKey extends keyof typeof formState>(
      field: TKey,
      value: (typeof formState)[TKey]
    ) => {
      // FR-012: any field change reverts a shown result to not-yet-tested.
      setTestResult(prev =>
        prev.status === "IDLE" ? prev : buildInitialNrtTestResult()
      );
      // A stale API error no longer describes the values being edited.
      setSubmitError(null);
      if (field === "expirationDate") {
        setHasBlurredExpirationDate(false);
      }
      setFormState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // Unreachable while the form is invalid (Test is disabled, FR-014).
  const handleRunTest = useCallback(async () => {
    if (validationError) return;

    setTestResult({
      status: "RUNNING",
      timestamp: String(Math.floor(Date.now() / 1000)),
      message: "Validating this record against the NRT rules engine.",
      items: []
    });

    const result = await runNrtRecordTestCheck(formState);
    // FR-012: don't resurrect a stale result if a field edit reset to IDLE mid-check.
    setTestResult(prev => (prev.status === "RUNNING" ? result : prev));
  }, [validationError, formState]);

  const handleSave = useCallback(async () => {
    setHasAttemptedSubmit(true);
    if (validationError) return;

    setSubmitError(null);

    // Required fields are guaranteed non-blank by validationError above, but
    // several are typed `string | null` on formState (e.g. free-typed
    // expressions, multi-select classification) — hence the casts.
    const payload: CreateNrtRecordRequest = {
      accountId: formState.accountId,
      caseNumber: formState.caseNumber,
      classification: formState.classification as string,
      createdBy: formState.createdBy,
      expirationDate: toUnixTimestampString(formState.expirationDate),
      map: formState.map as string,
      notes: formState.notes,
      providerId: formState.providerId,
      select: formState.select as string,
      status: formState.status,
      tags: formState.tags,
      type: formState.type as NrtVehicleType
    };

    try {
      if (isAddMode) {
        await createNrtRecord(payload);
      } else {
        const id = initialValues?.id;
        if (!id) {
          throw new Error("Missing record id — cannot update this record.");
        }
        const { createdBy, ...updateFields } = payload;
        const updatePayload: UpdateNrtRecordRequest = { id, ...updateFields };
        await updateNrtRecord(updatePayload);
      }

      const message = isAddMode
        ? "Record created successfully!"
        : "Record updated successfully!";
      toastSuccess("Success", message);
      // TODO: this announcement never fires — onClose unmounts before it commits.
      setAnnouncement(message);
      onClose();
    } catch (error) {
      // FR: API error stays visible in the form and the form stays open.
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    }
  }, [
    validationError,
    isAddMode,
    formState,
    initialValues,
    createNrtRecord,
    updateNrtRecord,
    toastSuccess,
    onClose
  ]);

  return (
    <Modal
      show
      header={isAddMode ? "Add New Record" : "Edit Record"}
      // Overrides MUI's maxWidth and Paper/DialogContent double-scroll (both
      // default to overflow-y: auto) — DialogContent alone should scroll.
      size="fullScreen"
      sx={{
        "& .MuiDialog-paper": { maxWidth: "1400px", overflowY: "visible" }
      }}
      onHide={isProcessing ? undefined : onClose}
    >
      <Box aria-live="polite" role="status" sx={SR_ONLY_SX}>
        {announcement}
      </Box>
      <ModalContent>
        {submitError && (
          <Alert role="alert" sx={{ marginBottom: "1.5rem" }} type="error">
            {submitError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("accountId")}
              required
              // Identity field: set once at creation, not editable afterward.
              disabled={isProcessing || !isAddMode}
              id="nrt-record-account-id"
              label="Account ID"
              name="accountId"
              value={formState.accountId}
              onChange={e => updateField("accountId", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("caseNumber")}
              required
              disabled={isProcessing}
              id="nrt-record-case-number"
              label="Case Number"
              name="caseNumber"
              value={formState.caseNumber}
              onChange={e => updateField("caseNumber", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("providerId")}
              required
              disabled={isProcessing}
              id="nrt-record-provider-id"
              label="Provider ID"
              name="providerId"
              value={formState.providerId}
              onChange={e => updateField("providerId", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("createdBy")}
              required
              // Audit field: stamped at creation, not editable afterward.
              disabled={isProcessing || !isAddMode}
              id="nrt-record-created-by"
              label="Created By"
              name="createdBy"
              value={formState.createdBy}
              onChange={e => updateField("createdBy", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <SelectInput
              required
              disabled={isProcessing}
              displayDeselectOption={false}
              id="nrt-record-status"
              label="Status"
              name="status"
              options={STATUS_OPTIONS}
              value={formState.status}
              onChange={e => updateField("status", e.target.value as NrtStatus)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("expirationDate")}
              required
              disabled={isProcessing}
              id="nrt-record-expiration-date"
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formState.expirationDate ?? ""}
              onBlur={() => setHasBlurredExpirationDate(true)}
              onChange={e => updateField("expirationDate", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <ComboBox
              {...errorProps("classification")}
              multiple
              required
              disabled={isProcessing}
              id="nrt-record-classification"
              label="Classification"
              name="classification"
              options={CLASSIFICATION_OPTIONS}
              value={classificationComboBoxValue}
              onChange={e =>
                updateField(
                  "classification",
                  serializeClassifications(
                    e.target.value ?? []
                  ) as NrtClassification
                )
              }
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <SelectInput
              required
              disabled={isProcessing}
              displayDeselectOption={false}
              id="nrt-record-type"
              label="Type"
              name="type"
              options={TYPE_OPTIONS}
              value={formState.type ?? undefined}
              onChange={e =>
                updateField("type", e.target.value as NrtVehicleType)
              }
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              disabled={isProcessing}
              id="nrt-record-tags"
              label="Tags"
              name="tags"
              value={formState.tags ?? ""}
              onChange={e => updateField("tags", e.target.value)}
            />
          </Grid>

          {/* Rule expressions and notes can share space. */}
          <Grid size={{ md: 6, xs: 12 }}>
            <TextInput
              {...errorProps("select")}
              required
              disabled={isProcessing}
              id="nrt-record-select"
              label="Select"
              name="select"
              placeholder="e.g. misc01 == 'Active'"
              value={formState.select ?? ""}
              onChange={e => updateField("select", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <TextInput
              id="nrt-record-map"
              {...errorProps("map")}
              required
              disabled={isProcessing}
              label="Map"
              name="map"
              placeholder="e.g. wholesalePrice = 799"
              value={formState.map ?? ""}
              onChange={e => updateField("map", e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextArea
              disabled={isProcessing}
              id="nrt-record-notes"
              label="Notes"
              name="notes"
              rows={NOTES_ROWS}
              value={formState.notes ?? ""}
              onChange={e => updateField("notes", e.target.value)}
            />
          </Grid>
        </Grid>
        {testResult.status === "RUNNING" ? (
          // FR-007: the action row is hidden (not just disabled) while a
          // test runs. Save has its own loading state below instead.
          <Typography sx={{ marginBottom: "1rem" }} variant="body-md">
            Processing…
          </Typography>
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "1rem"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "nrt_mapping_tool_run_test"
              }}
              buttonStyle="secondary"
              disabled={isTestDisabled || isSaving}
              onClick={handleRunTest}
            >
              Test
            </Button>
            <SaveAndCancel
              analyticsEventName={
                isAddMode ? "NrtAddRecordForm" : "NrtEditRecordForm"
              }
              isDisabled={{
                save: (!!validationError && hasAttemptedSubmit) || isSaving
              }}
              saveButtonText={isSaving ? "Saving…" : "Save"}
              onCancel={onClose}
              onSave={handleSave}
            />
          </Box>
        )}
        <TestResultPanel result={testResult} />
      </ModalContent>
    </Modal>
  );
};
